import * as React from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import { UwLocationReview } from 'types/underwriting.type';
import PropertyTableDataBuilder from 'components/Underwriting/components/PropertyTable/PropertyTableDataBuilder';
import { useSelector } from 'react-redux';
import { getSovLocations } from 'application/redux/selectors';
import { getLocationOptionsData } from 'application/redux/selectors/locationOptions';
import { ItemTableData } from 'components/ItemTable/ItemTableData';
import {
    calculateEndorsementPremium,
    calculateFinalRate,
    calculateTotalPremiumFromRateAndLimit,
} from 'helpers/calculations';
import LrDialog from 'components/LrDialog';
import ItemTable from 'components/ItemTable';

interface Props {
    data: UwLocationReview[];
    isOpen: boolean;
    isModiedAdjustmentEndorsement: boolean;
    origEffectiveDate: string;
    origExpDate: string;
    newEffectiveDate: string;
    newExpDate: string;
    onClose: () => void;
    onSave: (locationData: UwLocationReview[]) => void;
}

const UWDetailsAdjustmentDialog: FunctionComponent<Props> = (props) => {
    const {
        data,
        isOpen,
        isModiedAdjustmentEndorsement,
        onClose,
        onSave,
        origEffectiveDate,
        origExpDate,
        newEffectiveDate,
        newExpDate,
    } = props;

    const locations = useSelector(getSovLocations);
    const locationOptions = useSelector(getLocationOptionsData);

    const [locationItemData, setLocationItemData] = useState<ItemTableData>({} as ItemTableData);
    const [locationData, setLocationData] = useState<UwLocationReview[]>(data);

    const unformat = (formattedValue: string, asType: string) => {
        if (formattedValue === '-') return formattedValue;
        return asType === 'float'
            ? parseFloat(formattedValue.replace(/[^0-9-.]/g, ''))
            : parseInt(formattedValue.replace(/[^0-9-.]/g, ''), 10);
    };

    const changeMade = (value: string, id: string) => {
        const rowId = id.split(':')[0];
        const cellName = id.split(':')[1];
        const existingRowDataIdx = locationData.findIndex((entry: any) => entry.rowId.toString() === rowId);
        const foundRow = { ...locationData[existingRowDataIdx] };
        const floatFields = ['baseRate', 'CR_DB', 'triaPercentage'];

        foundRow[cellName] = floatFields.indexOf(cellName) !== -1 ? unformat(value, 'float') : value;
        foundRow.finalRate = calculateFinalRate(foundRow.CR_DB, foundRow.baseRate);
        foundRow.totalPremium = calculateTotalPremiumFromRateAndLimit(
            foundRow.finalRate,
            foundRow.totalInsurableValue,
            foundRow.triaPercentage,
            newEffectiveDate ? newEffectiveDate : origEffectiveDate,
            newExpDate ? newExpDate : origExpDate
        );
        foundRow.EQ_LIMIT = foundRow.EQ?.toUpperCase() === 'YES' ? foundRow.EQ_LIMIT : '$0';
        foundRow.FL_LIMIT = foundRow.FL?.toUpperCase() === 'YES' ? foundRow.FL_LIMIT : '$0';
        if (isModiedAdjustmentEndorsement) {
            foundRow.endorsementPremium = foundRow.totalPremium;
        }
        locationData[existingRowDataIdx] = foundRow;
        setLocationData([...locationData]);
    };

    useEffect(() => {
        if (locationData) {
            setLocationItemData(
                PropertyTableDataBuilder.build(
                    {},
                    locations,
                    locationOptions,
                    changeMade,
                    locationData,
                    false,
                    isModiedAdjustmentEndorsement,
                    false,
                    newEffectiveDate,
                    newExpDate
                )
            );
        }
    }, [locationData]);

    useEffect(() => {
        setLocationData(data);
    }, [data]);

    return (
        <LrDialog
            title={'UW Review Details'}
            isOpen={isOpen}
            onSubmit={() => onSave(locationData)}
            onCancel={onClose}
            submitButtonText={'Save'}
            cancelButtonText={'Cancel'}
            width={'1280px'}
        >
            <div style={{ width: '100%', overflowX: 'scroll' }}>
                <ItemTable
                    data={locationItemData}
                    defaultHeaderCellStyle={{ textAlign: 'right' }}
                    defaultBodyCellStyle={{ textAlign: 'right' }}
                />
            </div>
        </LrDialog>
    );
};

export default UWDetailsAdjustmentDialog;
